/**
 * Personal life timeline.
 *
 * One focal person, top-to-bottom: birth → death (or "still living"),
 * with every life event in between as a labelled dot. Events come from
 * four sources, ordered by trustworthiness:
 *   1. Birth + death (the bookends, from persons.birth_date / death_date)
 *   2. Family events (marriages, children's births) — derived from
 *      tree_relationships passed in via props
 *   3. Documents (every person_document with a doc_date) — fetched
 *   4. Historical events overlapping the life window — fetched, drawn
 *      as a faded background band so the personal events stay primary
 *
 * Anyone without a birth_date has nothing to anchor to, so they get a
 * clear empty state asking the user to add one. A picker lets the user
 * switch focal person without leaving the tab.
 */
import { useEffect, useMemo, useState } from 'react'
import { extractYear as extractYearStr } from '../../utils/date.js'
import { api, mediaUrl } from '../../services/api.js'
import './Timeline.css'

const extractYear = (str) => {
  const y = extractYearStr(str)
  return y ? parseInt(y, 10) : null
}

const PX_PER_YEAR = 18  // vertical density — tweak for visual rhythm

const DOC_TYPE_LABEL = {
  birth_cert: 'Birth certificate',
  death_cert: 'Death certificate',
  marriage_cert: 'Marriage certificate',
  census: 'Census record',
  military: 'Military record',
  photo: 'Photograph',
  newspaper: 'Newspaper clipping',
  letter: 'Letter',
  other: 'Document',
}

const KIND_ICON = {
  birth:        '🌱',
  death:        '✝',
  marriage:     '💍',
  child:        '👶',
  document:     '📄',
  historical:   '🌍',
}

export default function Timeline({ persons, relationships = [], focalPersonId, onPersonClick }) {
  // Pick the focal: explicit prop wins; otherwise oldest person with a
  // birth date; otherwise nothing.
  const focal = useMemo(() => {
    if (focalPersonId) {
      const f = persons.find(p => p.id === focalPersonId)
      if (f) return f
    }
    const dated = persons.filter(p => extractYear(p.birth_date))
    if (dated.length) {
      return dated.sort((a, b) => extractYear(a.birth_date) - extractYear(b.birth_date))[0]
    }
    return null
  }, [persons, focalPersonId])

  const [documents, setDocuments] = useState([])
  const [historical, setHistorical] = useState([])

  useEffect(() => {
    if (!focal?.id) { setDocuments([]); setHistorical([]); return }
    let cancelled = false
    Promise.all([
      api.get(`/persons/${focal.id}/documents`).catch(() => []),
      api.get(`/persons/${focal.id}/historical-events`).catch(() => []),
    ]).then(([docs, hist]) => {
      if (!cancelled) {
        setDocuments(Array.isArray(docs) ? docs : [])
        setHistorical(Array.isArray(hist) ? hist : [])
      }
    })
    return () => { cancelled = true }
  }, [focal?.id])

  if (!persons.length) return (
    <div className="empty-state" style={{ paddingTop: '4rem' }}>
      <div className="empty-state__icon">📅</div>
      <h3>No persons yet</h3>
      <p>Add people to your tree to see a personal life timeline.</p>
    </div>
  )

  if (!focal) return (
    <div className="empty-state" style={{ paddingTop: '4rem' }}>
      <div className="empty-state__icon">📅</div>
      <h3>No birth dates yet</h3>
      <p>Pick a person and add a birth date to see their personal timeline.</p>
    </div>
  )

  const birthYear = extractYear(focal.birth_date)
  if (!birthYear) return (
    <div className="lifeline">
      <FocalHeader focal={focal} persons={persons} onPickFocal={onPersonClick} />
      <div className="empty-state" style={{ paddingTop: '3rem' }}>
        <div className="empty-state__icon">🗓️</div>
        <h3>No birth date for {focal.first_name || 'this person'}</h3>
        <p>Add a birth date on their profile and the timeline will populate.</p>
      </div>
    </div>
  )

  const deathYear = extractYear(focal.death_date)
  // If no death date is recorded but the person was born more than ~100
  // years ago, they're effectively never alive today — so don't draw the
  // timeline running to "117 years and counting." Cap at birth + 90 and
  // mark the end as "presumed deceased."
  const currentYear = new Date().getFullYear()
  const presumedDeceased = !deathYear && birthYear < currentYear - 100
  const endYear = deathYear
    ? deathYear
    : presumedDeceased
      ? Math.min(birthYear + 90, currentYear)
      : currentYear
  const lifespan = Math.max(endYear - birthYear, 1)

  // ── Build the events list ─────────────────────────────────────────────
  const events = []

  events.push({
    kind: 'birth',
    year: birthYear,
    date: focal.birth_date,
    title: 'Born',
    detail: focal.birth_place || null,
    age: 0,
  })

  if (deathYear) {
    events.push({
      kind: 'death',
      year: deathYear,
      date: focal.death_date,
      title: 'Died',
      detail: focal.death_place || null,
      age: deathYear - birthYear,
    })
  }

  // Marriages — partner relationships involving the focal person.
  // start_date is on the relationship; the partner is the other person.
  for (const r of relationships) {
    if (r.type !== 'partner') continue
    if (r.person_a_id !== focal.id && r.person_b_id !== focal.id) continue
    const partnerId = r.person_a_id === focal.id ? r.person_b_id : r.person_a_id
    const partner = persons.find(p => p.id === partnerId)
    const mYear = extractYear(r.start_date) || extractYear(r.marriage_date)
    if (!mYear) continue
    const partnerName = partner
      ? [partner.first_name, partner.last_name].filter(Boolean).join(' ')
      : 'Unknown partner'
    events.push({
      kind: 'marriage',
      year: mYear,
      date: r.start_date,
      title: `Married ${partnerName}`,
      detail: r.marriage_place || null,
      partnerId,
      age: mYear - birthYear,
    })
  }

  // Children's births — parent_child rels where the focal is the parent.
  for (const r of relationships) {
    if (r.type !== 'parent_child') continue
    if (r.person_a_id !== focal.id) continue
    const child = persons.find(p => p.id === r.person_b_id)
    if (!child) continue
    const cYear = extractYear(child.birth_date)
    if (!cYear) continue
    const childName = [child.first_name, child.last_name].filter(Boolean).join(' ')
    events.push({
      kind: 'child',
      year: cYear,
      date: child.birth_date,
      title: `${childName ? childName + ' born' : 'Child born'}`,
      detail: child.birth_place || null,
      childId: child.id,
      age: cYear - birthYear,
    })
  }

  // Documents — only those with a doc_date.
  for (const d of documents) {
    const dYear = extractYear(d.doc_date)
    if (!dYear) continue
    events.push({
      kind: 'document',
      year: dYear,
      date: d.doc_date,
      title: DOC_TYPE_LABEL[d.doc_type] || 'Document',
      detail: d.source_label || null,
      docId: d.id,
      age: dYear - birthYear,
    })
  }

  events.sort((a, b) => (a.year - b.year) || (a.kind === 'birth' ? -1 : b.kind === 'birth' ? 1 : 0))

  // Historical events — overlap the life window. Lane-pack them so
  // overlapping events stack into separate vertical columns on the right
  // side of the board (instead of crashing all their labels at the top
  // of overlapping background bands).
  const histClamped = historical
    .map(h => ({
      ...h,
      bandStart: Math.max(h.start_year || birthYear, birthYear),
      bandEnd:   Math.min(h.end_year || h.start_year || endYear, endYear),
    }))
    .filter(b => b.bandStart <= b.bandEnd && b.bandEnd >= birthYear && b.bandStart <= endYear)
    .sort((a, b) => a.bandStart - b.bandStart || a.bandEnd - b.bandEnd)

  const MAX_LANES = 4
  const laneEnds = []  // laneEnds[i] = the latest year occupied by lane i
  const lifeBands = []
  for (const h of histClamped) {
    let lane = laneEnds.findIndex(y => y < h.bandStart)
    if (lane === -1) {
      if (laneEnds.length >= MAX_LANES) continue  // skip overflow events
      lane = laneEnds.length
      laneEnds.push(h.bandEnd)
    } else {
      laneEnds[lane] = h.bandEnd
    }
    lifeBands.push({ ...h, lane })
  }
  const laneCount = Math.min(laneEnds.length, MAX_LANES)

  // ── Layout ────────────────────────────────────────────────────────────
  const totalHeight = lifespan * PX_PER_YEAR
  const yearToY = (y) => (y - birthYear) * PX_PER_YEAR

  // Year ticks every 5 years for short lives, every 10 for long ones.
  const tickEvery = lifespan > 60 ? 10 : 5
  const ticks = []
  for (let y = Math.ceil(birthYear / tickEvery) * tickEvery; y <= endYear; y += tickEvery) {
    if (y > birthYear && y < endYear) ticks.push(y)
  }

  return (
    <div className="lifeline">
      <FocalHeader focal={focal} persons={persons} onPickFocal={onPersonClick} />

      <div className="lifeline__meta">
        <span className="lifeline__years">
          {birthYear}{deathYear ? ` – ${deathYear}` : presumedDeceased ? ' – ?' : ' – present'}
        </span>
        <span className="lifeline__sep">·</span>
        <span className="lifeline__lifespan">
          {deathYear ? `lived ${lifespan} years`
            : presumedDeceased ? 'death date unknown — presumed deceased'
            : `${lifespan} years and counting`}
        </span>
        {events.length > 1 && (
          <>
            <span className="lifeline__sep">·</span>
            <span>{events.length - (deathYear ? 2 : 1)} life event{events.length - (deathYear ? 2 : 1) === 1 ? '' : 's'}</span>
          </>
        )}
      </div>

      <div
        className="lifeline__board"
        style={{ height: `${totalHeight + 80}px` }}
      >
        {/* Year ticks */}
        {ticks.map(y => (
          <div key={`tick-${y}`} className="lifeline__tick" style={{ top: `${yearToY(y) + 24}px` }}>
            <span className="lifeline__tick-label">{y}</span>
          </div>
        ))}

        {/* The vertical life line */}
        <div className="lifeline__axis" />

        {/* Personal events on the line. Don't extend under the historical
            strip: right offset matches the strip's width so cards stop
            cleanly before the bars. */}
        {events.map((ev, i) => (
          <EventDot
            key={`ev-${i}-${ev.kind}-${ev.year}`}
            event={ev}
            top={yearToY(ev.year) + 24}
            rightInset={laneCount > 0 ? laneCount * 22 + 12 : 0}
            onClick={() => {
              if (ev.kind === 'child' && ev.childId) onPersonClick?.(ev.childId)
              if (ev.kind === 'marriage' && ev.partnerId) onPersonClick?.(ev.partnerId)
            }}
          />
        ))}

        {/* Historical context strip — one thin lane per concurrent event,
            on the right edge. Doesn't compete with personal events. */}
        {lifeBands.length > 0 && (
          <div className="lifeline__hist-strip" style={{ width: `${laneCount * 22}px` }}>
            {lifeBands.map((b, i) => {
              const top = yearToY(b.bandStart) + 24
              const height = Math.max(yearToY(b.bandEnd) - yearToY(b.bandStart), 6)
              const label = `${b.title} (${b.start_year}${b.end_year && b.end_year !== b.start_year ? '–' + b.end_year : ''})${b.summary ? ' — ' + b.summary : ''}`
              return (
                <div
                  key={`hb-${b.id || i}`}
                  className="lifeline__hist-bar"
                  style={{ top: `${top}px`, height: `${height}px`, left: `${b.lane * 22}px` }}
                  title={label}
                >
                  <span className="lifeline__hist-label">{b.title}</span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

function FocalHeader({ focal, persons, onPickFocal }) {
  const [showPicker, setShowPicker] = useState(false)
  const name = [focal.first_name, focal.last_name].filter(Boolean).join(' ') || 'This person'

  // Sorted picker — by birth year, then by name
  const pickable = useMemo(() => {
    return [...persons]
      .map(p => ({ ...p, _by: extractYear(p.birth_date) || 9999 }))
      .sort((a, b) => a._by - b._by || (a.last_name || '').localeCompare(b.last_name || ''))
  }, [persons])

  return (
    <div className="lifeline__header">
      <div className="lifeline__avatar">
        {focal.photo_thumb_key
          ? <img src={mediaUrl(focal.photo_thumb_key)} alt={name} />
          : (focal.first_name?.[0]?.toUpperCase() || '?')}
      </div>
      <div className="lifeline__title-block">
        <h2 className="lifeline__title">{name}</h2>
        <button type="button" className="lifeline__pick" onClick={() => setShowPicker(s => !s)}>
          {showPicker ? 'Close picker' : 'Pick a different person'}
        </button>
        {showPicker && (
          <div className="lifeline__picker">
            {pickable.map(p => {
              const pname = [p.first_name, p.last_name].filter(Boolean).join(' ') || '(unnamed)'
              const by = extractYear(p.birth_date)
              const dy = extractYear(p.death_date)
              const years = by ? (dy ? `${by}–${dy}` : `b. ${by}`) : '—'
              return (
                <button
                  key={p.id}
                  type="button"
                  className={`lifeline__picker-item${p.id === focal.id ? ' is-active' : ''}`}
                  onClick={() => { onPickFocal?.(p.id); setShowPicker(false) }}
                >
                  <span>{pname}</span>
                  <span className="lifeline__picker-years">{years}</span>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

function EventDot({ event, top, onClick, rightInset = 0 }) {
  const ageLabel = event.kind === 'birth'
    ? '0'
    : event.age != null && event.age >= 0 ? `age ${event.age}` : null
  const clickable = event.kind === 'child' || event.kind === 'marriage'

  return (
    <div
      className={`lifeline__event lifeline__event--${event.kind}${clickable ? ' is-clickable' : ''}`}
      style={{ top: `${top}px`, right: `${rightInset}px` }}
      onClick={clickable ? onClick : undefined}
    >
      <div className="lifeline__event-year">
        <span className="lifeline__event-y">{event.year}</span>
        {ageLabel && <span className="lifeline__event-age">{ageLabel}</span>}
      </div>
      <div className="lifeline__event-dot" aria-hidden="true">
        {KIND_ICON[event.kind] || '•'}
      </div>
      <div className="lifeline__event-card">
        <div className="lifeline__event-title">{event.title}</div>
        {event.detail && <div className="lifeline__event-detail">{event.detail}</div>}
      </div>
    </div>
  )
}
